import React from "react";
import { ExpandMore, WineBar, SmokingRooms, SmokeFree, FitnessCenter } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Stack,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import dayjs from "dayjs";
import { alcoholicIntakeOptions } from "./alcoholicIntakeOptions";
import {
  drugTypeLifetimeOptions,
  physicalActivityTypeOptions,
  alcoholFrequencyOptions,
  smokingOptions,
  drugFrequencyPast12MonthsOptions,
  physicalActivityIntensityOptions,
} from "./constants/options";

const translateCode: any = {
  wk: "semanas",
  mo: "meses",
  "mL/(24.h)": "mL/24h",
  "ml/(24.h)": "mL/24h",
  "mL/d": "mL/dia",
  "ml/d": "mL/dia",
  "d/wk": "dias/semana",
  min: "minutos",
};

const observationLabelOverrides: Record<string, string> = {
  "72166-2": "Tabagismo",
  "74013-4": "Consumo diário de álcool",
  "68518-0": "Frequência de consumo de álcool",
  "63643-1": "Tipo de droga usado na sua vida",
  "96843-8": "Quantas vezes você usou drogas ilícitas ou recreativas nos últimos 12 meses",
  "68515-6": "Dias por semana de exercício moderado a intenso",
  "68516-4": "Minutos de exercício moderado a intenso nos dias exercitados",
  "81685-9": "Tipo de atividade física",
  "89555-7": "Intensidade usual da atividade",
};

const alcoholicCodeLabelMap = alcoholicIntakeOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.label;
    return acc;
  },
  {}
);

const alcoholicTypeCodeSet = new Set(
  alcoholicIntakeOptions.map((option) => option.code)
);

const drugTypeCodeLabelMap = drugTypeLifetimeOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.label;
    return acc;
  },
  {}
);

const physicalActivityTypeCodeLabelMap = physicalActivityTypeOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.label;
    return acc;
  },
  {}
);

const alcoholFrequencyCodeMap = alcoholFrequencyOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.display;
    acc[option.value] = option.display;
    return acc;
  },
  {}
);

const smokingCodeMap = smokingOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.display;
    acc[option.value] = option.display;
    return acc;
  },
  {}
);

const drugFrequencyCodeMap = drugFrequencyPast12MonthsOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.display;
    acc[option.value] = option.display;
    return acc;
  },
  {}
);

const physicalActivityIntensityCodeMap = physicalActivityIntensityOptions.reduce(
  (acc: Record<string, string>, option) => {
    acc[option.code] = option.label;
    acc[option.value] = option.label;
    return acc;
  },
  {}
);

const alcoholCodes = ["74013-4", "68518-0", ...alcoholicIntakeOptions.map((opt) => opt.code)];
const smokingCodes = ["72166-2"];
const drugsCodes = ["63643-1", "96843-8"];
const exerciseCodes = ["68515-6", "68516-4", "81685-9", "89555-7"];

const verifyValue = (el: any) => {
  const observationCode = el?.resource?.code?.coding?.[0]?.code;

  if (el?.resource?.code?.coding?.[0]?.code === "55284-4") {
    return `${el?.resource?.component?.[0]?.valueQuantity?.value || "-"
      }/${el?.resource?.component?.[1]?.valueQuantity?.value || "-"
      } mmHg`;
  }

  if (el?.resource?.hasOwnProperty("valueCodeableConcept")) {
    const code = el?.resource?.valueCodeableConcept?.coding?.[0]?.code;
    const display = el?.resource?.valueCodeableConcept?.coding?.[0]?.display;
    const text = el?.resource?.valueCodeableConcept?.text;

    if (observationCode === "68518-0") {
      return alcoholFrequencyCodeMap[code as string] ||
        alcoholFrequencyCodeMap[display as string] ||
        display ||
        text ||
        "-";
    }
    if (observationCode === "72166-2") {
      return smokingCodeMap[code as string] ||
        smokingCodeMap[display as string] ||
        display ||
        text ||
        "-";
    }
    if (observationCode === "96843-8") {
      return drugFrequencyCodeMap[code as string] ||
        drugFrequencyCodeMap[display as string] ||
        display ||
        text ||
        "-";
    }
    if (observationCode === "89555-7") {
      return physicalActivityIntensityCodeMap[code as string] ||
        physicalActivityIntensityCodeMap[display as string] ||
        display ||
        text ||
        "-";
    }

    return display || text || "-";
  }

  if (el?.resource?.hasOwnProperty("valueQuantity")) {
    const value = el?.resource?.valueQuantity?.value;
    const code = el?.resource?.valueQuantity?.code;
    const unit =
      el?.resource?.valueQuantity?.unit ||
      (code ? translateCode[code] || code : undefined);

    if (value !== undefined && unit !== undefined) {
      return `${value} ${unit}`;
    }
    if (value !== undefined) {
      return `${value}`;
    }
    if (unit !== undefined) {
      return `${unit}`;
    }
  }
  if (el?.resource?.hasOwnProperty("valueDateTime")) {
    return dayjs(el?.resource?.valueDateTime).format("DD/MM/YYYY");
  }

  return "-";
};

const ObservationItem = ({ label, value }: { label: string; value: string }) => (
  <Stack
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    direction="row"
    sx={{
      mt: 1.5,
      mb: 1.5,
      py: 1,
      px: 2,
      borderRadius: 2,
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
      },
    }}
  >
    <Typography
      variant="body2"
      fontWeight={500}
      color="text.primary"
      sx={{ flex: 1 }}
    >
      {label}
    </Typography>
    <Paper
      elevation={0}
      sx={{
        minWidth: "180px",
        maxWidth: "250px",
        borderRadius: 2,
        padding: 1.5,
        backgroundColor: (theme) => theme.palette.mode === "light"
          ? "rgba(25, 118, 210, 0.08)"
          : "rgba(25, 118, 210, 0.16)",
      }}
    >
      <Typography
        variant="body2"
        textAlign="center"
        fontWeight={600}
        color="primary.dark"
        sx={{ wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Paper>
  </Stack>
);

const SectionHeader = ({ title, icon }: { title: string; icon: React.ReactNode }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1.5}
    sx={{
      mb: 2,
      py: 1,
      px: 2,
      borderRadius: 2,
      backgroundColor: (theme) => theme.palette.mode === "light"
        ? "rgba(25, 118, 210, 0.1)"
        : "rgba(25, 118, 210, 0.2)",
      borderLeft: "2px solid",
      borderColor: "primary400.main",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: "primary400.main",
        color: "white",
      }}
    >
      {icon}
    </Box>
    <Typography variant="body1" fontWeight={500} color="neutral700.main">
      {title}
    </Typography>
  </Stack>
);

export function SocialHistoryAccordion({ data, index }: any) {
  const alcoholRecords = data?.records?.filter((el: any) =>
    alcoholCodes.includes(el?.resource?.code?.coding?.[0]?.code)
  ) ?? [];

  const smokingRecords = data?.records?.filter((el: any) =>
    smokingCodes.includes(el?.resource?.code?.coding?.[0]?.code)
  ) ?? [];

  const drugsRecords = data?.records?.filter((el: any) =>
    drugsCodes.includes(el?.resource?.code?.coding?.[0]?.code)
  ) ?? [];

  const exerciseRecords = data?.records?.filter((el: any) =>
    exerciseCodes.includes(el?.resource?.code?.coding?.[0]?.code)
  ) ?? [];

  const beverageObservations = alcoholRecords.filter((el: any) =>
    alcoholicTypeCodeSet.has(el?.resource?.code?.coding?.[0]?.code)
  );

  const beverageLabels = Array.from(
    new Set(
      beverageObservations
        .map((el: any) => {
          const code = el?.resource?.code?.coding?.[0]?.code;
          return (
            alcoholicCodeLabelMap[code as string] ||
            el?.resource?.code?.text ||
            el?.resource?.code?.coding?.[0]?.display
          );
        })
        .filter(Boolean)
    )
  );

  const otherAlcoholRecords = alcoholRecords.filter(
    (el: any) => !alcoholicTypeCodeSet.has(el?.resource?.code?.coding?.[0]?.code)
  );

  const drugTypeObservations = drugsRecords.filter(
    (el: any) =>
      el?.resource?.code?.coding?.[0]?.code === "63643-1" &&
      el?.resource?.valueCodeableConcept?.coding?.[0]?.code
  );

  const drugTypeLabels = Array.from(
    new Set(
      drugTypeObservations
        .map((el: any) => {
          const code = el?.resource?.valueCodeableConcept?.coding?.[0]?.code;
          return (
            drugTypeCodeLabelMap[code as string] ||
            el?.resource?.valueCodeableConcept?.coding?.[0]?.display ||
            el?.resource?.valueCodeableConcept?.text
          );
        })
        .filter(Boolean)
    )
  );

  const otherDrugsRecords = drugsRecords.filter(
    (el: any) => el?.resource?.code?.coding?.[0]?.code !== "63643-1"
  );

  const physicalActivityTypeObservations = exerciseRecords.filter(
    (el: any) =>
      el?.resource?.code?.coding?.[0]?.code === "81685-9" &&
      el?.resource?.valueCodeableConcept?.coding?.[0]?.code
  );

  const physicalActivityTypeLabels = Array.from(
    new Set(
      physicalActivityTypeObservations
        .map((el: any) => {
          const code = el?.resource?.valueCodeableConcept?.coding?.[0]?.code;
          return (
            physicalActivityTypeCodeLabelMap[code as string] ||
            el?.resource?.valueCodeableConcept?.coding?.[0]?.display ||
            el?.resource?.valueCodeableConcept?.text
          );
        })
        .filter(Boolean)
    )
  );

  const otherExerciseRecords = exerciseRecords.filter(
    (el: any) => el?.resource?.code?.coding?.[0]?.code !== "81685-9"
  );

  return (
    <Accordion
      defaultExpanded={index === 0}
      sx={{
        width: "100%",
        boxShadow: "1px 1px 16px 1px rgba(0, 0, 0, 0.1)",
        position: "initial",
        border: "1px solid",
        borderColor: "neutral200.main",
        borderRadius: 1,
        "& .MuiAccordionDetails-root": {
          maxHeight: "calc(100vh - 300px)",
          overflow: "auto",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Stack direction="row" width="100%" spacing={2}>
          <Chip
            label={
              data?.encounter?.resource?.serviceType?.[0]?.reference?.display ||
              "Serviço não encontrado"
            }
            size="small"
            sx={{
              color: data?.encounter?.resource?.serviceType?.[0]?.reference
                ?.display
                ? "neutral700.main"
                : "neutral500.main",

              fontWeight: 500,
              backgroundColor: data?.displayInfo?.healthcare ? "" : "#f1f1f1",
            }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 3 }}>
        {(otherAlcoholRecords.length > 0 || beverageLabels.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <SectionHeader
              title="Consumo Alcoólico"
              icon={<WineBar sx={{ fontSize: "18px" }} />}
            />
            <Box sx={{ pl: 1 }}>
              {otherAlcoholRecords.map((el: any, recordIndex: number) => {
                const observationCode = el?.resource?.code?.coding?.[0]?.code;
                const auxLabel =
                  observationLabelOverrides[observationCode as string] ||
                  el?.resource?.code?.text ||
                  el?.resource?.code?.coding?.[0]?.display;
                const auxValue = verifyValue(el);
                const recordKey =
                  el?.resource?.id ||
                  `${el?.resource?.code?.coding?.[0]?.code || "unknown"}-${recordIndex}`;

                return (
                  <ObservationItem key={recordKey} label={auxLabel} value={auxValue} />
                );
              })}
              {beverageLabels.length > 0 && (
                <ObservationItem
                  label="Tipos de bebidas alcoólicas"
                  value={beverageLabels.join(", ")}
                />
              )}
            </Box>
          </Box>
        )}

        {smokingRecords.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <SectionHeader
              title="Tabagismo"
              icon={<SmokingRooms sx={{ fontSize: "18px" }} />}
            />
            <Box sx={{ pl: 1 }}>
              {smokingRecords.map((el: any, recordIndex: number) => {
                const observationCode = el?.resource?.code?.coding?.[0]?.code;
                const auxLabel =
                  observationLabelOverrides[observationCode as string] ||
                  el?.resource?.code?.text ||
                  el?.resource?.code?.coding?.[0]?.display;
                const auxValue = verifyValue(el);
                const recordKey =
                  el?.resource?.id ||
                  `${el?.resource?.code?.coding?.[0]?.code || "unknown"}-${recordIndex}`;

                return (
                  <ObservationItem key={recordKey} label={auxLabel} value={auxValue} />
                );
              })}
            </Box>
          </Box>
        )}

        {(otherDrugsRecords.length > 0 || drugTypeLabels.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <SectionHeader
              title="Drogas Ilícitas"
              icon={<SmokeFree sx={{ fontSize: "18px" }} />}
            />
            <Box sx={{ pl: 1 }}>
              {drugTypeLabels.length > 0 && (
                <ObservationItem
                  label="Tipos de drogas usadas na vida"
                  value={drugTypeLabels.join(", ")}
                />
              )}
              {otherDrugsRecords.map((el: any, recordIndex: number) => {
                const observationCode = el?.resource?.code?.coding?.[0]?.code;
                const auxLabel =
                  observationLabelOverrides[observationCode as string] ||
                  el?.resource?.code?.text ||
                  el?.resource?.code?.coding?.[0]?.display;
                const auxValue = verifyValue(el);
                const recordKey =
                  el?.resource?.id ||
                  `${el?.resource?.code?.coding?.[0]?.code || "unknown"}-${recordIndex}`;

                return (
                  <ObservationItem key={recordKey} label={auxLabel} value={auxValue} />
                );
              })}
            </Box>
          </Box>
        )}

        {(otherExerciseRecords.length > 0 || physicalActivityTypeLabels.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <SectionHeader
              title="Exercícios Físicos"
              icon={<FitnessCenter sx={{ fontSize: "18px" }} />}
            />
            <Box sx={{ pl: 1 }}>
              {otherExerciseRecords.map((el: any, recordIndex: number) => {
                const observationCode = el?.resource?.code?.coding?.[0]?.code;
                const auxLabel =
                  observationLabelOverrides[observationCode as string] ||
                  el?.resource?.code?.text ||
                  el?.resource?.code?.coding?.[0]?.display;
                const auxValue = verifyValue(el);
                const recordKey =
                  el?.resource?.id ||
                  `${el?.resource?.code?.coding?.[0]?.code || "unknown"}-${recordIndex}`;

                return (
                  <ObservationItem key={recordKey} label={auxLabel} value={auxValue} />
                );
              })}
              {physicalActivityTypeLabels.length > 0 && (
                <ObservationItem
                  label="Tipos de atividade física"
                  value={physicalActivityTypeLabels.join(", ")}
                />
              )}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }}></Divider>
        <Typography
          color="neutral700.main"
          sx={{ fontWeight: 500, fontStyle: "italic", fontSize: 12 }}
        >
          Dr.{" "}
          {
            data?.encounter?.resource?.participant.find((el: any) =>
              el?.actor?.reference.includes("Practitioner")
            ).actor?.display
          }
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
